/**
 * Stride-driven locomotion phase sync — pure math (the slide-proof recipe).
 *
 * The blended stride length is the weight-average of the contributing clips' authored strides;
 * cycle frequency = ground speed / stride. By construction, foot speed then equals ground speed
 * at every blend point: Σwᵢ·strideᵢ × cycleHz === speed. The bridge writes each action's .time
 * directly from one shared phase (timeScale 0), so blended clips' footfalls coincide via their
 * per-clip phase offsets instead of beating against each other.
 */
export interface LocomotionContribution {
  weight: number;
  strideLengthM: number;
}

export function blendedStride(contribs: LocomotionContribution[]): number {
  let w = 0;
  let s = 0;
  for (const c of contribs) {
    w += c.weight;
    s += c.weight * c.strideLengthM;
  }
  return w > 1e-6 ? s / w : 0;
}

/**
 * `maxHz` caps the cycle rate: the gait clips were verified (direct stance-foot sweep
 * measurement) to represent slow gaits (walk ~1.0, run ~1.9, boost ~3.5 m/s), so exact foot
 * matching at 7 m/s demands frantic cadence. Above the cap, foot slide grows exactly
 * proportionally (slideFraction = 1 − maxHz·stride/speed) — the honest trade, panel-tunable.
 */
/** `surfaceMul` < 1 slows the foot cycle BELOW ground speed — deliberate, visible foot-slip
 *  (ice skating reads because the feet glide; exact foot-matching on ice would look wrong). */
export function cycleHz(speed: number, stride: number, maxHz = Infinity, surfaceMul = 1): number {
  if (stride <= 1e-6 || speed < 0.05) return 0;
  return Math.min(speed / stride, maxHz) * surfaceMul;
}

export function advancePhase(phase01: number, hz: number, dt: number): number {
  const next = (phase01 + hz * dt) % 1;
  return next < 0 ? next + 1 : next;
}

/** The clip-local normalized time for a shared phase and a clip's footfall offset. */
export function clipTime01(phase01: number, phaseOffset01: number): number {
  return (phase01 + phaseOffset01) % 1;
}
